/**
 * The 32 positions, generated rather than hand-drawn.
 *
 * Universal numbering (1–32). Both arches are walked by arc length along an ellipse, and each
 * position is allotted a share of that length proportional to its real relative width — so molars
 * get more room than incisors and the spacing reads as an arch rather than a row of equal boxes.
 *
 * Coordinates are rounded to 3 decimals on purpose: trigonometric results are not bit-identical
 * between Node and a browser engine, and an unrounded transform string fails hydration on the
 * landing site's static export.
 */

export type Arch = 'upper' | 'lower';
export type Side = 'right' | 'left';
export type ToothType = 'incisor' | 'canine' | 'premolar' | 'molar';

export interface ToothPosition {
  /** Universal number, 1–32. */
  readonly number: number;
  readonly arch: Arch;
  /** The client's own right and left, never the viewer's. */
  readonly side: Side;
  readonly type: ToothType;
  /** "Third molar", "Lateral incisor", … */
  readonly name: string;
  /** Position within its quadrant, 1 (furthest back) to 8 (midline). */
  readonly ordinal: number;
  /** Centre of the position in the arch's coordinate space. */
  readonly x: number;
  readonly y: number;
  /** Degrees of rotation so the crown points away from the centre of the arch. */
  readonly angle: number;
  /** Allotted width, in the same units as x/y. */
  readonly width: number;
  /** Announced by screen readers, e.g. "upper right first molar, position 3". */
  readonly accessibleName: string;
}

/** Left-to-right on screen. The client's right side appears on the viewer's left, as in a mirror. */
export const UPPER_DISPLAY_ORDER: readonly number[] = [
  1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16,
];
export const LOWER_DISPLAY_ORDER: readonly number[] = [
  32, 31, 30, 29, 28, 27, 26, 25, 24, 23, 22, 21, 20, 19, 18, 17,
];

const NAMES_BY_ORDINAL: readonly string[] = [
  'Third molar',
  'Second molar',
  'First molar',
  'Second premolar',
  'First premolar',
  'Canine',
  'Lateral incisor',
  'Central incisor',
];

/** Relative crown widths. Molars are broad, lateral incisors narrow. */
const WIDTH_BY_ORDINAL: readonly number[] = [1.22, 1.24, 1.3, 1.0, 1.0, 0.95, 0.82, 1.0];

/** Position within the quadrant: 1 at the back of the mouth, 8 at the midline. */
export function quadrantOrdinal(toothNumber: number): number {
  if (toothNumber >= 1 && toothNumber <= 8) return toothNumber;
  if (toothNumber >= 9 && toothNumber <= 16) return 17 - toothNumber;
  if (toothNumber >= 17 && toothNumber <= 24) return toothNumber - 16;
  if (toothNumber >= 25 && toothNumber <= 32) return 33 - toothNumber;
  throw new Error(`Not a Universal tooth number: ${toothNumber}`);
}

export function archOf(toothNumber: number): Arch {
  return toothNumber <= 16 ? 'upper' : 'lower';
}

export function sideOf(toothNumber: number): Side {
  if (toothNumber <= 8) return 'right';
  if (toothNumber <= 16) return 'left';
  if (toothNumber <= 24) return 'left';
  return 'right';
}

export function typeOf(toothNumber: number): ToothType {
  const ordinal = quadrantOrdinal(toothNumber);
  if (ordinal <= 3) return 'molar';
  if (ordinal <= 5) return 'premolar';
  if (ordinal === 6) return 'canine';
  return 'incisor';
}

export function nameOf(toothNumber: number): string {
  return NAMES_BY_ORDINAL[quadrantOrdinal(toothNumber) - 1] ?? 'Tooth';
}

export interface ArchGeometry {
  /** Half-width of the arch. */
  readonly radiusX: number;
  /** Depth of the arch from midline to the back. */
  readonly radiusY: number;
  /** Centre of the ellipse. */
  readonly centerX: number;
  readonly centerY: number;
  /** How far around the ellipse the arch sweeps, in radians, measured from the midline. */
  readonly sweep: number;
}

export const DEFAULT_UPPER_GEOMETRY: ArchGeometry = {
  radiusX: 420,
  radiusY: 250,
  centerX: 500,
  centerY: 300,
  sweep: 1.28,
};

export const DEFAULT_LOWER_GEOMETRY: ArchGeometry = {
  radiusX: 385,
  radiusY: 230,
  centerX: 500,
  centerY: 340,
  sweep: 1.3,
};

const round = (value: number): number => Math.round(value * 1000) / 1000;

interface CurvePoint {
  readonly x: number;
  readonly y: number;
  readonly t: number;
}

/**
 * Sample the arch densely, then walk it by cumulative arc length so each position sits at the
 * centre of its own allotted run of curve. Equal angular steps would crowd the front teeth.
 */
function sampleArch(geometry: ArchGeometry, arch: Arch, samples = 2000): CurvePoint[] {
  const points: CurvePoint[] = [];
  const direction = arch === 'upper' ? -1 : 1;

  for (let i = 0; i <= samples; i += 1) {
    const t = -geometry.sweep + (2 * geometry.sweep * i) / samples;
    points.push({
      x: geometry.centerX + geometry.radiusX * Math.sin(t),
      y: geometry.centerY + direction * geometry.radiusY * Math.cos(t),
      t,
    });
  }

  return points;
}

function cumulativeLengths(points: readonly CurvePoint[]): number[] {
  const lengths = [0];
  for (let i = 1; i < points.length; i += 1) {
    const previous = points[i - 1];
    const current = points[i];
    if (!previous || !current) continue;
    const dx = current.x - previous.x;
    const dy = current.y - previous.y;
    lengths.push((lengths[i - 1] ?? 0) + Math.hypot(dx, dy));
  }
  return lengths;
}

function pointAtLength(
  points: readonly CurvePoint[],
  lengths: readonly number[],
  target: number,
): CurvePoint {
  const last = points[points.length - 1];
  if (!last) throw new Error('Arch was not sampled');

  for (let i = 1; i < lengths.length; i += 1) {
    const lower = lengths[i - 1] ?? 0;
    const upper = lengths[i] ?? 0;
    if (upper < target) continue;

    const a = points[i - 1];
    const b = points[i];
    if (!a || !b) break;

    const span = upper - lower;
    const ratio = span === 0 ? 0 : (target - lower) / span;
    return {
      x: a.x + (b.x - a.x) * ratio,
      y: a.y + (b.y - a.y) * ratio,
      t: a.t + (b.t - a.t) * ratio,
    };
  }

  return last;
}

/**
 * Build one arch. Positions come back in display order, left to right on screen.
 */
export function buildArch(arch: Arch, geometry?: ArchGeometry): ToothPosition[] {
  const resolved = geometry ?? (arch === 'upper' ? DEFAULT_UPPER_GEOMETRY : DEFAULT_LOWER_GEOMETRY);
  const order = arch === 'upper' ? UPPER_DISPLAY_ORDER : LOWER_DISPLAY_ORDER;

  const points = sampleArch(resolved, arch);
  const lengths = cumulativeLengths(points);
  const totalLength = lengths[lengths.length - 1] ?? 0;

  const weights = order.map((number) => WIDTH_BY_ORDINAL[quadrantOrdinal(number) - 1] ?? 1);
  const weightSum = weights.reduce((sum, weight) => sum + weight, 0);

  const positions: ToothPosition[] = [];
  let travelled = 0;

  order.forEach((number, index) => {
    const weight = weights[index] ?? 1;
    const span = (weight / weightSum) * totalLength;
    const centre = travelled + span / 2;
    travelled += span;

    const point = pointAtLength(points, lengths, centre);
    // The crown points away from the centre of the ellipse; t is the parametric angle there.
    const angle = arch === 'upper' ? (point.t * 180) / Math.PI : (point.t * 180) / Math.PI + 180;

    const side = sideOf(number);
    const name = nameOf(number);
    const ordinal = quadrantOrdinal(number);

    positions.push({
      number,
      arch,
      side,
      type: typeOf(number),
      name,
      ordinal,
      x: round(point.x),
      y: round(point.y),
      angle: round(angle),
      width: round(span),
      accessibleName: `${arch} ${side} ${name.toLowerCase()}, position ${String(number)}`,
    });
  });

  return positions;
}

/** All 32 positions, upper arch first, each in display order. */
export function buildDentition(): ToothPosition[] {
  return [...buildArch('upper'), ...buildArch('lower')];
}

/** The generated set, computed once. */
export const TOOTH_POSITIONS: readonly ToothPosition[] = buildDentition();

export const TOOTH_POSITIONS_BY_NUMBER: ReadonlyMap<number, ToothPosition> = new Map(
  TOOTH_POSITIONS.map((position) => [position.number, position]),
);

export function toothPosition(toothNumber: number): ToothPosition {
  const position = TOOTH_POSITIONS_BY_NUMBER.get(toothNumber);
  if (!position) throw new Error(`Not a Universal tooth number: ${toothNumber}`);
  return position;
}

/**
 * How the side is described to a client. Never the chart convention borrowed from practice —
 * it carries a forbidden noun and means nothing to the person reading it.
 */
export function viewerSideLabel(side: Side): string {
  return side === 'right' ? 'Your right' : 'Your left';
}
